import React, { useState, useEffect } from 'react';
import { RealtimeDBService } from '../firebase/unifiedDataService';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import QuestionEditor from '../components/QuestionEditor';
import GeneralSettings from '../components/admin/GeneralSettings';
import GamificationSettings from '../components/admin/GamificationSettings';
import { Card } from '../components/admin';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Logger } from '../utils/logger';
import { uiStorage } from '../services/storageService';

const log = Logger.create('QuestionSetView');

// Question Set Editor View
function QuestionSetView() {
  // Get sessionId from URL params (if editing specific quiz)
  const { sessionId: urlSessionId } = useParams();
  // Get query params (e.g., ?edit=0 to edit question at index 0)
  const [searchParams] = useSearchParams();
  const editQuestionIndex = searchParams.get('edit') !== null ? parseInt(searchParams.get('edit'), 10) : null;
  
  // Use Firebase Auth (Unified)
  const { user, loading: authLoading, login, logout, error: authError, clearError } = useUnifiedAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Session data
  const [sessionId, setSessionId] = useState('');
  const [existingSessions, setExistingSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);
  const [error, setError] = useState('');

  // Question data (managed by QuestionEditor component)
  const [questions, setQuestions] = useState([]);
  
  // Quiz Settings - matching all options from GamificationSettings
  const [quizSettings, setQuizSettings] = useState({
    enableGamification: true,
    enableTimeBonus: true,
    showLeaderboard: true,
    showStreak: true,
    lockOnReveal: true,
    enableQuestionTimer: false,
    questionTimerSeconds: 30,
    basePointsPerQuestion: 100,
    showIntroScreen: false,
    showMultipleChoiceHint: true,
    anonymousMode: false
  });

  // Check if authenticated via Firebase
  const isAuthenticated = !!user;

  // Handle login with Firebase Auth
  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoginError('');
    clearError();
    
    try {
      await login(email, password);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  // Restore selected session from localStorage on mount, or load from URL
  useEffect(() => {
    const loadSessionData = async () => {
      if (!isAuthenticated) return;
      
      const sessionToLoad = urlSessionId || uiStorage.get('currentEditingQuiz');
      
      // Only load if we have a sessionId and it's different from current
      if (sessionToLoad && sessionToLoad !== selectedSession) {
        await loadQuiz(sessionToLoad);
      }
    };
    
    loadSessionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, urlSessionId]);

  // Fetch sessions when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchExistingSessions();
    }
  }, [isAuthenticated]);

  // Fetch existing quiz sessions
  const fetchExistingSessions = async () => {
    try {
      const sessionsData = await RealtimeDBService.get('sessions');
      if (sessionsData) {
        const sessionsArray = Object.keys(sessionsData).map(key => {
          let firstQuestionText = "Keine Fragen";
          
          if (sessionsData[key].questions && sessionsData[key].questions.length > 0) {
            firstQuestionText = sessionsData[key].questions[0].question;
          }
          
          return {
            id: key,
            questionCount: sessionsData[key].questions?.length || 0,
            firstQuestion: firstQuestionText.substring(0, 30) + (firstQuestionText.length > 30 ? "..." : ""),
          };
        });
        
        setExistingSessions(sessionsArray);
      }
    } catch (error) {
      log.error('Error fetching sessions:', error);
      setError("Fehler beim Laden der Quizze");
    }
  };

  // Load selected quiz
  const loadQuiz = async (sessionId) => {
    try {
      const data = await RealtimeDBService.get(`sessions/${sessionId}`);
      if (data) {
        if (data.questions) {
          setQuestions(data.questions);
        } else {
          setQuestions([]);
        }
        setSelectedSession(sessionId);
        uiStorage.set('currentEditingQuiz', sessionId);
        
        // Load settings if they exist
        if (data.settings) {
          setQuizSettings({
            enableGamification: data.settings.enableGamification ?? true,
            enableTimeBonus: data.settings.enableTimeBonus ?? true,
            showLeaderboard: data.settings.showLeaderboard ?? true,
            showStreak: data.settings.showStreak ?? true,
            lockOnReveal: data.settings.lockOnReveal ?? true,
            enableQuestionTimer: data.settings.enableQuestionTimer ?? false,
            questionTimerSeconds: data.settings.questionTimerSeconds ?? 30,
            basePointsPerQuestion: data.settings.basePointsPerQuestion ?? 100,
            showIntroScreen: data.settings.showIntroScreen ?? false,
            showMultipleChoiceHint: data.settings.showMultipleChoiceHint ?? true,
            anonymousMode: data.settings.anonymousMode ?? false
          });
        }
      } else {
        setError("Quiz nicht gefunden");
      }
    } catch (error) {
      log.error('Error loading quiz:', error);
      setError("Fehler beim Laden des Quiz: " + error.message);
    }
  };

  // Create new quiz
  const createNewQuiz = async () => {
    if (!sessionId.trim()) {
      setError("Bitte Quiz-Namen eingeben");
      return;
    }
    
    const newSessionId = sessionId.toLowerCase().replace(/\s+/g, '-');
    
    try {
      // Check if session already exists
      const existingSession = await RealtimeDBService.get(`sessions/${newSessionId}`);
      
      if (existingSession) {
        setError("Ein Quiz mit diesem Namen existiert bereits");
      } else {
        // Create new session with settings
        await RealtimeDBService.set(`sessions/${newSessionId}`, {
          questions: [],
          answers: {},
          active: true,
          currentQuestion: 0,
          showResults: false,
          createdAt: Date.now(),
          settings: quizSettings
        });
        // Redirect to edit the new quiz
        setRedirectTo(`/editor/${newSessionId}`);
      }
    } catch (err) {
      setError("Fehler beim Erstellen des Quiz: " + err.message);
    }
  };

  // Handle questions change from QuestionEditor component
  const handleQuestionsChange = async (newQuestions) => {
    setQuestions(newQuestions);
    
    // Save to Firebase using RealtimeDBService
    try {
      await RealtimeDBService.update(`sessions/${selectedSession}`, {
        questions: newQuestions
      });
      setError('');
    } catch (err) {
      setError("Fehler beim Speichern der Fragen: " + err.message);
    }
  };

  // Handle settings change
  const handleSettingsChange = async (updates) => {
    const updatedSettings = {...quizSettings, ...updates};
    setQuizSettings(updatedSettings);
    
    // Save to Firebase if a session is selected
    if (selectedSession) {
      try {
        await RealtimeDBService.update(`sessions/${selectedSession}`, {
          settings: updatedSettings
        });
        setError('');
      } catch (err) {
        setError("Fehler beim Speichern der Einstellungen: " + err.message);
      }
    }
  };

  // Navigate to admin view (live mode)
  const goToAdminView = () => {
    if (selectedSession) {
      setRedirectTo(`/live/${selectedSession}`);
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      log.error('Logout error:', err);
    }
  };

  if (redirectTo) {
    return <Navigate to={redirectTo} />;
  }

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="p-6 max-w-md mx-auto bg-white rounded shadow mt-10">
        <h1 className="text-2xl font-bold mb-4">Authentifizierung wird geprüft...</h1>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="p-6 max-w-md mx-auto bg-white rounded shadow mt-10">
        <h1 className="text-2xl font-bold mb-4">Quiz-Editor Login</h1>
        <p className="text-gray-600 mb-4 text-sm">Melde dich mit deinem Firebase-Account an</p>
        
        {(loginError || authError) && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {loginError || authError}
          </div>
        )}
        
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">E-Mail</label>
            <input 
              type="email" 
              placeholder="admin@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Passwort</label>
            <input 
              type="password" 
              placeholder="Passwort"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          
          <button 
            type="submit"
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          >
            Anmelden
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <a href="/" className="text-blue-600 hover:underline text-sm">← Zurück zur Startseite</a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white rounded shadow mt-10">
      {/* Admin Header - only show navigation when at hub (no quiz selected) */}
      {!selectedSession ? (
        <div className="mb-6 pb-4 border-b">
          <h1 className="text-2xl font-bold mb-4">Quiz-Editor</h1>
          
          {/* Admin Tools Navigation */}
          <div className="grid grid-cols-3 gap-4">
            <a 
              href="/editor" 
              className="flex flex-col items-center p-4 bg-blue-50 rounded-lg border-2 border-blue-500"
            >
              <span className="font-semibold text-gray-800">Fragen-Editor</span>
            </a>
            
            <a 
              href="/llm-generator" 
              className="flex flex-col items-center p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 theme-transition"
            >
              <span className="text-3xl mb-2">🤖</span>
              <span className="font-semibold text-gray-800">KI Generator</span>
            </a>
            
            <a 
              href="/database" 
              className="flex flex-col items-center p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 theme-transition"
            >
              <span className="text-3xl mb-2">🗄️</span>
              <span className="font-semibold text-gray-800">Datenbank</span>
            </a>
          </div>
        </div>
      ) : (
        <div className="mb-4 flex items-center justify-between">
          <a 
            href="/editor" 
            onClick={() => {
              setSelectedSession(null);
              uiStorage.remove('currentEditingQuiz');
            }}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            ← Zurück zur Quiz-Übersicht
          </a>
          {editQuestionIndex !== null && (
            <a 
              href={`/live/${selectedSession}`}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium"
            >
              ← Zurück zur Live-Ansicht
            </a>
          )}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {!selectedSession ? (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Quiz auswählen oder erstellen</h2>
          
          <div className="mb-6">
            <h3 className="text-md font-medium mb-2">Neues Quiz erstellen</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Quiz-Name (wird auch zur URL)</label>
                <input
                  type="text"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  placeholder="z.B. javascript-grundlagen"
                  className="w-full p-2 border rounded"
                />
                <p className="text-xs text-gray-500 mt-1">Link wird: /quiz/{sessionId || 'quiz-name'}</p>
              </div>
              
              {/* General Settings */}
              <Card title="Allgemein" collapsible defaultCollapsed={false}>
                <GeneralSettings
                  settings={quizSettings}
                  onUpdateSettings={handleSettingsChange}
                />
              </Card>
              
              {/* Gamification Settings */}
              <Card title="Gamification" icon="🎮" collapsible defaultCollapsed={false}>
                <GamificationSettings
                  settings={quizSettings}
                  onUpdateSettings={handleSettingsChange}
                />
              </Card>
              
              <button
                onClick={createNewQuiz}
                className="w-full bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600"
              >
                Quiz erstellen
              </button>
            </div>
          </div>
          
          <div>
            <h3 className="text-md font-medium mb-2">Existierendes Quiz bearbeiten</h3>
            {existingSessions.length === 0 ? (
              <p className="text-gray-500">Keine existierenden Quizze gefunden</p>
            ) : (
              <div className="bg-gray-50 rounded border p-2 max-h-60 overflow-y-auto">
                {existingSessions.map(session => (
                  <div key={session.id} className="border-b py-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">{session.id}</div>
                        <div className="text-xs text-gray-500">
                          {session.questionCount} Fragen • {session.firstQuestion}
                        </div>
                      </div>
                      <a
                        href={`/editor/${session.id}`}
                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200"
                      >
                        Bearbeiten
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold">
              Quiz bearbeiten: <span className="text-blue-600">{selectedSession}</span>
            </h2>
            <div className="space-x-2">
              <a
                href="/llm-generator"
                className="inline-block bg-purple-600 text-white py-1 px-3 rounded text-sm hover:bg-purple-700"
              >
                🤖 KI Generator
              </a>
              <button
                onClick={goToAdminView}
                className="bg-purple-600 text-white py-1 px-3 rounded text-sm hover:bg-purple-700"
              >
                Admin-Ansicht öffnen
              </button>
              <button
                onClick={() => {
                  setSelectedSession(null);
                  setQuestions([]);
                  uiStorage.remove('currentEditingQuiz');
                }}
                className="bg-gray-200 text-gray-700 py-1 px-3 rounded text-sm hover:bg-gray-300"
              >
                Anderes Quiz wählen
              </button>
            </div>
          </div>
          
          {/* General Settings - Editable for existing quiz */}
          <Card title="Allgemein" collapsible defaultCollapsed={true} className="mb-4">
            <GeneralSettings
              settings={quizSettings}
              onUpdateSettings={handleSettingsChange}
            />
          </Card>
          
          {/* Gamification Settings - Editable for existing quiz */}
          <Card title="Gamification" icon="🎮" collapsible defaultCollapsed={true} className="mb-6">
            <GamificationSettings
              settings={quizSettings}
              onUpdateSettings={handleSettingsChange}
            />
          </Card>
          
          {/* Question Editor Component */}
          <QuestionEditor
            questions={questions}
            onQuestionsChange={handleQuestionsChange}
            sessionId={selectedSession}
            onError={setError}
            initialEditIndex={editQuestionIndex}
          />
        </div>
      )}
      
      <div className="border-t pt-4 flex justify-between items-center">
        <div className="space-x-4">
          <a 
            href="/"
            className="text-blue-600 hover:underline"
          >
            Zurück zum Start
          </a>
          <a 
            href="/llm-generator"
            className="text-purple-600 hover:underline font-medium"
          >
            🤖 KI Fragen-Generator
          </a>
        </div>
        <button
          onClick={handleLogout}
          className="text-red-600 hover:underline"
        >
          Abmelden
        </button>
      </div>
    </div>
  );
}

export default QuestionSetView;